import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { isMarketProductType, isMarketSubcategory, MARKET_CATEGORY_BY_CODE, marketCategoryLabel, marketLeafLabel, SHOW_CATEGORY_COUNTS } from '../lib/marketCategories';
import { fromLegacy } from '../lib/marketTaxonomy';
import { fetchFacets, fetchShowcase, searchMarket } from '../lib/marketCatalog';
import { activeCount, clearFilters, facetGroups, hasRetiredParams, readFilters, SORTS, toggleAttr, writeFilters } from '../lib/marketFilters';
import { attributeLabel, attributeValueLabel } from '../lib/marketAttributes';
import MarketShell, { Icon, Thumb } from '../components/market/MarketShell';
import SourceResults from '../components/market/SourceResults';
import { searchSourcesByText } from '../lib/marketImageSearch';
import { CardRow, ServicesPanel } from './MarketHome';
import { track, screenScrollPct } from '../lib/growthTracker';

// All products, search, category and supplier pages of the marketplace:
//   /market/products (every listed product; /market/categories lands here)
//   /market/search?q=…   /market/c/:cat   /market/c/:cat/:sub   /market/c/:cat/:sub/:type
//   /market/search?seller=…
// Categories are a FILTER here (an expandable main → subcategory tree, and
// chips above the grid), not the content: the grid is always products.
// :cat is a main category, :sub a subcategory and :type a product type of the
// taxonomy (lib/marketTaxonomy.js); an address with codes it does not have
// falls back to the deepest valid level, and an earlier code is moved.
// The URL holds every filter (lib/marketFilters.js) — compact keys only, so a
// result page can be shared and the back button works. Filters come from the
// structured attribute registry (lib/marketAttributes.js): one clear attribute
// per group, natural Arabic/English labels, counts that respect the other
// selected filters, and only groups that can actually narrow the results.
// A search also reaches MAABAR's suppliers for products not in the catalog yet
// (market-source-search, owner decision 2026-10-22): by itself when the catalog
// has fewer than SOURCE_AUTO_BELOW results, behind a button otherwise. Visitors
// included; the server limits each searcher and reuses the same words' results.

const T = {
  ar: { market: 'السوق', search: 'نتائج البحث', all: 'كل المنتجات', results: (n) => `${n.toLocaleString('en-US')} منتج`, more: 'عرض المزيد',
    filters: 'التصفية', clearAll: 'مسح الكل', apply: 'تطبيق', show: (n) => `عرض ${n.toLocaleString('en-US')} منتج`, showAll: 'عرض المنتجات', close: 'إغلاق',
    sort: 'الترتيب', sorts: { relevance: 'الأكثر صلة', price_asc: 'السعر: من الأقل', price_desc: 'السعر: من الأعلى', rating: 'تقييم المورد', newest: 'الأحدث' },
    price: 'السعر للوحدة (ر.س)', min: 'من', max: 'إلى', moq: 'الحد الأدنى للطلب', moqUpTo: (n) => `حتى ${n}`, any: 'الكل',
    rating: 'تقييم المورد', ratingAtLeast: (n) => `${n} فأعلى`, buying: 'طريقة الشراء', variants: 'بخيارات متعددة', tiers: 'أسعار جملة حسب الكمية',
    priceChip: (a, b) => (a != null && b != null ? `${a}–${b} ر.س` : a != null ? `من ${a} ر.س` : `حتى ${b} ر.س`), moqChip: (n) => `حد أدنى ≤ ${n}`, ratingChip: (n) => `تقييم ${n}+`,
    allOf: 'تجمع كل ما تختاره', showMore: (n) => `عرض ${n} أخرى`, showLess: 'عرض أقل', categories: 'الأقسام', supplier: 'منتجات المورد',
    noResults: 'لا توجد منتجات تطابق اختيارك.', noResultsTitle: 'لم نجد هذا المنتج في السوق — نوفّره لك من المصانع مباشرة', clearTry: 'امسح التصفية وجرّب مرة أخرى',
    oldLink: 'تم تحديث نظام التصفية، فعرضنا القسم دون التصفية القديمة.', searchIn: (x) => `ابحث في ${x}`,
    recommended: 'المقترح', allProducts: 'كل المنتجات', loadError: 'تعذّر تحميل المنتجات.', retry: 'إعادة المحاولة', expand: (x) => `عرض أقسام ${x}`,
    src: { title: 'منتجات أخرى قد تناسبك', btn: 'اعرض منتجات أخرى', loading: 'نبحث لك عن المزيد…', finding: (q) => `نبحث لك عن «${q}»…`,
      err: { rate_limited: 'انتظر لحظة ثم حاول مرة أخرى.', daily_limit: 'وصلت إلى حد البحث لليوم — جرّب غدًا.', busy: 'الخدمة مشغولة الآن — حاول بعد قليل.', provider_timeout: 'تأخر البحث — جرّب مرة ثانية.', failed: 'تعذّر جلب المنتجات، حاول مرة أخرى.' } } },
  en: { market: 'Market', search: 'Search results', all: 'All products', results: (n) => `${n.toLocaleString('en-US')} products`, more: 'Show more',
    filters: 'Filters', clearAll: 'Clear all', apply: 'Apply', show: (n) => `Show ${n.toLocaleString('en-US')} products`, showAll: 'Show products', close: 'Close',
    sort: 'Sort', sorts: { relevance: 'Most relevant', price_asc: 'Price: low to high', price_desc: 'Price: high to low', rating: 'Supplier rating', newest: 'Newest' },
    price: 'Unit price (SAR)', min: 'Min', max: 'Max', moq: 'Minimum order', moqUpTo: (n) => `Up to ${n}`, any: 'Any',
    rating: 'Supplier rating', ratingAtLeast: (n) => `${n} & up`, buying: 'Buying options', variants: 'Has variants', tiers: 'Wholesale price tiers',
    priceChip: (a, b) => (a != null && b != null ? `SAR ${a}–${b}` : a != null ? `From SAR ${a}` : `Up to SAR ${b}`), moqChip: (n) => `MOQ ≤ ${n}`, ratingChip: (n) => `Rating ${n}+`,
    allOf: 'Matches all you pick', showMore: (n) => `Show ${n} more`, showLess: 'Show less', categories: 'Categories', supplier: 'Supplier products',
    noResults: 'No products match your selection.', noResultsTitle: 'Not in the market yet — we can source it from factories for you', clearTry: 'Clear filters and try again',
    oldLink: 'Filters have been updated; we opened the category without the old filters.', searchIn: (x) => `Search in ${x}`,
    recommended: 'Recommended', allProducts: 'All products', loadError: 'Products could not be loaded.', retry: 'Try again', expand: (x) => `Show ${x} subcategories`,
    src: { title: 'More products for you', btn: 'Show more products', loading: 'Finding more for you…', finding: (q) => `Finding «${q}» for you…`,
      err: { rate_limited: 'Wait a moment and try again.', daily_limit: 'You have reached today\'s search limit — try again tomorrow.', busy: 'The service is busy — try again shortly.', provider_timeout: 'The search took too long — try again.', failed: 'Products could not be loaded — please try again.' } } },
};
T.zh = T.en;
const PAGE = 24;
const SOURCE_AUTO_BELOW = 10;
const SOURCE_KEY = 'mk_src_text_v2:';
const VISIBLE = 6;

function Group({ title, note, children, defaultOpen = true, id }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mk-fgroup" data-fgroup={id}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <span>{title}{note && <small> · {note}</small>}</span><Icon.Chevron />
      </button>
      {open && <div className="mk-fbody">{children}</div>}
    </div>
  );
}

function Options({ values, onToggle, c, attr, kind = 'checkbox' }) {
  const [all, setAll] = useState(false);
  const shown = all ? values : values.slice(0, VISIBLE);
  return (
    <>
      {shown.map((v) => (
        <label key={v.value} className={`mk-opt${v.count === 0 && !v.selected ? ' zero' : ''}`} data-facet-value={`${attr}.${v.value}`}>
          <input type={kind} checked={!!v.selected} onChange={() => onToggle(v.value)} name={kind === 'radio' ? attr : undefined} />
          <span className="lbl">{v.label}</span>
          {SHOW_CATEGORY_COUNTS && v.count != null && <span className="mk-count">{v.count}</span>}
        </label>
      ))}
      {values.length > VISIBLE && <button type="button" className="mk-fmore" onClick={() => setAll((x) => !x)}>{all ? c.showLess : c.showMore(values.length - VISIBLE)}</button>}
    </>
  );
}

/** Categories as a filter: every main category with products, each expandable to its subcategories. */
function CategoryTree({ show, lang, category, sub, onGo, c }) {
  const [open, setOpen] = useState(category || null);
  useEffect(() => { setOpen(category || null); }, [category]);
  const mains = Object.entries(show).filter(([, v]) => v.total > 0).sort((a, b) => b[1].total - a[1].total);
  return (
    <div className="mk-ctree" data-category-tree={mains.length}>
      <button type="button" className={`mk-ctree-item${!category ? ' on' : ''}`} data-ctree="all" aria-current={!category} onClick={() => onGo('')}>
        <span>{c.allProducts}</span>
      </button>
      {mains.map(([code, v]) => {
        const subs = (MARKET_CATEGORY_BY_CODE[code]?.groups || []).filter((g) => v.leaves?.[g.code]?.count > 0);
        const isOpen = open === code;
        return (
          <div key={code} className="mk-ctree-main">
            <div className="mk-ctree-row">
              <button type="button" className={`mk-ctree-item${category === code && !sub ? ' on' : ''}`} data-ctree={code} aria-current={category === code && !sub} onClick={() => onGo(code)}>
                <span>{marketCategoryLabel(code, lang)}</span>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{v.total}</span>}
              </button>
              {subs.length > 0 && (
                <button type="button" className="mk-ctree-toggle" aria-expanded={isOpen} aria-label={c.expand(marketCategoryLabel(code, lang))} data-ctree-toggle={code}
                  onClick={() => setOpen(isOpen ? null : code)}><Icon.Chevron /></button>
              )}
            </div>
            {isOpen && (
              <div className="mk-ctree-subs">
                {subs.map((g) => (
                  <button key={g.code} type="button" className={`mk-ctree-item sub${sub === g.code ? ' on' : ''}`} data-ctree={`${code}/${g.code}`} aria-current={sub === g.code} onClick={() => onGo(code, g.code)}>
                    <span>{lang === 'ar' ? g.ar : g.en}</span>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{v.leaves[g.code].count}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function MarketProducts({ lang = 'ar', user, exchangeRates }) {
  const nav = useNavigate();
  const { cat = '', sub: subParam = '', type: typeParam = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const c = T[lang] || T.ar;
  usePageTitle('market', lang);
  const f = useMemo(() => readFilters(params), [params]);
  const seller = params.get('seller') || '';
  const category = cat && MARKET_CATEGORY_BY_CODE[cat] ? cat : '';
  const sub = category && isMarketSubcategory(subParam, category) ? subParam : '';
  const type = sub && isMarketProductType(typeParam, sub) ? typeParam : '';
  // Addresses under the earlier category codes land on their new place.
  useEffect(() => {
    if (category || !cat) return;
    const moved = fromLegacy(subParam || cat);
    if (moved) nav(`/market/c/${[moved.main, moved.sub, moved.type].filter(Boolean).join('/')}${params.toString() ? `?${params}` : ''}`, { replace: true });
  }, [category, cat, subParam]); // eslint-disable-line react-hooks/exhaustive-deps
  const [oldLink, setOldLink] = useState(false);

  // An old bookmark (raw supplier specs in the URL) opens the same page, unfiltered, and says so once.
  useEffect(() => {
    if (!hasRetiredParams(params)) return;
    setOldLink(true);
    const next = writeFilters(f);
    if (seller) next.set('seller', seller);
    setParams(next, { replace: true });
  }, [params, f, seller, setParams]);

  const apply = (next) => {
    const p = writeFilters(next);
    if (seller) p.set('seller', seller);
    setParams(p);
  };
  const query = useMemo(() => ({ ...f, category: category || null, subcategory: sub || null, productType: type || null, seller: seller || null }), [f, category, sub, type, seller]);
  const key = JSON.stringify(query);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [moreBusy, setMoreBusy] = useState(false);
  const sentinel = useRef(null);
  const [facets, setFacets] = useState([]);
  const [show, setShow] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [price, setPrice] = useState({ min: '', max: '' });
  useEffect(() => { setPrice({ min: f.priceMin ?? '', max: f.priceMax ?? '' }); }, [f.priceMin, f.priceMax]);
  useEffect(() => { fetchShowcase().then(setShow); }, []);
  // Growth OS (step 3): what a search or category returned, what was opened,
  // and a search left with nothing opened.
  const gx = useRef({ started: 0, shownAt: 0, clicked: false });
  useEffect(() => {
    let alive = true;
    gx.current.started = Date.now();
    setLoading(true); setFailed(false);
    Promise.all([searchMarket({ ...query, limit: PAGE, offset: 0 }), fetchFacets(query)]).then(([r, fc]) => {
      if (!alive) return;
      if (r.error) console.warn('[MarketProducts] market_search failed:', r.error.message);
      setFailed(!!r.error); setRows(r.rows); setTotal(r.total); setFacets(fc.facets); setLoading(false);
    });
    return () => { alive = false; };
  }, [key, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDrawer(false); }, [cat, sub, type]);
  useEffect(() => {
    if (loading || failed || seller) return undefined;
    const g = gx.current;
    g.shownAt = Date.now(); g.clicked = false;
    const q = (f.q || '').trim();
    if (q) {
      track('search_results_shown', { query: q, result_count: total, zero_results: total === 0, product_ids: rows.slice(0, 20).map((p) => p.id), latency_ms: g.shownAt - g.started });
    } else if (category) {
      track('category_viewed', { category, subcategory: sub || type || undefined, product_count: total });
    }
    return () => {
      if (q && !g.clicked) {
        track('search_abandoned', { query: q, result_count: total, seconds_on_results: Math.round((Date.now() - g.shownAt) / 1000), scroll_depth: screenScrollPct() });
      }
    };
  }, [loading, failed, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const onResultClick = (e) => {
    if (!e.target.closest('.mk-card-media, .mk-card-title')) return;
    const id = e.target.closest('[data-market-card]')?.dataset.marketCard;
    if (!id) return;
    gx.current.clicked = true;
    const q = (f.q || '').trim();
    if (q) track('search_result_clicked', { query: q, product_id: id, position: rows.findIndex((p) => p.id === id) + 1, page: Math.floor(rows.findIndex((p) => p.id === id) / PAGE) + 1 });
  };

  // The suppliers, for the same words. Kept per tab so Back from a product
  // shows them again without a second search.
  const srcQ = f.q && !seller ? f.q.trim() : '';
  const [src, setSrc] = useState({ q: '', state: 'idle', results: [], page: 0, more: false, err: '' });
  const srcFor = useRef('');   // the words on screen: a late answer for other words is dropped
  const runSource = async (q, page = 1) => {
    srcFor.current = q;
    setSrc((x) => (page === 1 ? { q, state: 'loading', results: [], page: 0, more: false, err: '' } : { ...x, state: 'more' }));
    let r = await searchSourcesByText(q, page);
    // One quiet retry: a dropped connection, or two searches inside the server's two seconds.
    if (!r.ok && ['network_error', 'rate_limited', 'failed', 'provider_timeout'].includes(r.error)) {
      await new Promise((res) => setTimeout(res, 2500));
      if (srcFor.current !== q) return;
      r = await searchSourcesByText(q, page);
    }
    if (srcFor.current !== q) return;
    setSrc((x) => {
      if (!r.ok) return page === 1 ? { q, state: 'error', results: [], page: 0, more: false, err: c.src.err[r.error] || c.src.err.failed } : { ...x, state: 'done', more: false };
      const next = { q, state: 'done', results: page === 1 ? r.results : [...x.results, ...r.results], page, more: r.more, err: '' };
      try { sessionStorage.setItem(SOURCE_KEY + q, JSON.stringify(next)); } catch { /* storage full or blocked */ }
      return next;
    });
  };
  useEffect(() => {
    if (!srcQ || loading || failed) { if (!srcQ) { srcFor.current = ''; setSrc({ q: '', state: 'idle', results: [], page: 0, more: false, err: '' }); } return; }
    if (src.q === srcQ && src.state !== 'idle') return;
    try {
      const kept = JSON.parse(sessionStorage.getItem(SOURCE_KEY + srcQ) || 'null');
      if (kept?.results) { srcFor.current = srcQ; setSrc({ ...kept, q: srcQ, state: 'done' }); return; }
    } catch { /* nothing kept */ }
    if (total < SOURCE_AUTO_BELOW) runSource(srcQ);
    else { srcFor.current = srcQ; setSrc({ q: srcQ, state: 'idle', results: [], page: 0, more: false, err: '' }); }
  }, [srcQ, loading, failed, total]); // eslint-disable-line react-hooks/exhaustive-deps

  const more = async () => {
    if (moreBusy) return;
    setMoreBusy(true);
    const r = await searchMarket({ ...query, limit: PAGE, offset: rows.length });
    setRows((x) => [...x, ...r.rows.filter((p) => !x.some((y) => y.id === p.id))]);
    setMoreBusy(false);
  };
  // Infinite scroll: the next page loads as the end of the grid comes into view
  // (the "show more" button stays, for keyboards and as a fallback).
  useEffect(() => {
    const el = sentinel.current;
    if (!el || loading || rows.length >= total || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) more(); }, { rootMargin: '600px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loading, rows.length, total, moreBusy]); // eslint-disable-line react-hooks/exhaustive-deps
  const goCategory = (code, subCode) => nav(`${code ? `/market/c/${[code, subCode].filter(Boolean).join('/')}` : '/market/products'}${qs(writeFilters({ ...f, attrs: {} }))}`);
  const groups = useMemo(() => facetGroups(facets, { category: category || null, total, selected: f.attrs, lang }), [facets, category, total, f.attrs, lang]);
  const meta = category ? MARKET_CATEGORY_BY_CODE[category] : null;
  // Progressive navigation: a category page offers its subcategories, a
  // subcategory (or product-type) page its product types. Only what is listed.
  const here = show?.[category];
  const tiles = !here ? [] : sub
    ? (meta.groups.find((g) => g.code === sub)?.types || []).filter((t) => here.leaves[sub]?.types?.[t.code]?.count > 0)
      .map((t) => ({ code: t.code, to: `/market/c/${category}/${sub}/${t.code}`, label: lang === 'ar' ? t.ar : t.en, ...here.leaves[sub].types[t.code], current: type === t.code }))
    : meta.groups.filter((g) => here.leaves[g.code]?.count > 0)
      .map((g) => ({ code: g.code, to: `/market/c/${category}/${g.code}`, label: lang === 'ar' ? g.ar : g.en, ...here.leaves[g.code], current: false }));
  const allTile = sub ? { to: `/market/c/${category}/${sub}`, images: here?.leaves[sub]?.images, count: here?.leaves[sub]?.count, current: !type }
    : { to: `/market/c/${category}`, images: here?.images, count: here?.total, current: true };
  const title = seller ? `${c.supplier}: ${seller}` : type ? marketLeafLabel(type, lang) : sub ? marketLeafLabel(sub, lang) : category ? marketCategoryLabel(category, lang) : f.q ? `${c.search}: «${f.q}»` : c.allProducts;
  const scopeLabel = type ? marketLeafLabel(type, lang) : sub ? marketLeafLabel(sub, lang) : category ? marketCategoryLabel(category, lang) : '';
  const nActive = activeCount(f);
  function qs(p) { return p.toString() ? `?${p}` : ''; }

  // Active filter chips — every one removable on its own.
  const chips = [];
  if (f.priceMin != null || f.priceMax != null) chips.push({ k: 'price', label: c.priceChip(f.priceMin, f.priceMax), off: { ...f, priceMin: null, priceMax: null } });
  if (f.moqMax != null) chips.push({ k: 'moq', label: c.moqChip(f.moqMax), off: { ...f, moqMax: null } });
  if (f.minRating != null) chips.push({ k: 'rating', label: c.ratingChip(f.minRating), off: { ...f, minRating: null } });
  if (f.hasVariants) chips.push({ k: 'variants', label: c.variants, off: { ...f, hasVariants: null } });
  if (f.hasTiers) chips.push({ k: 'tiers', label: c.tiers, off: { ...f, hasTiers: null } });
  for (const [a, vs] of Object.entries(f.attrs)) for (const v of vs) {
    const l = attributeValueLabel(a, v, lang);
    if (l) chips.push({ k: `${a}.${v}`, label: `${attributeLabel(a, lang)}: ${l}`, off: toggleAttr(f, a, v) });
  }

  const panel = (
    <div className="mk-filters-inner" data-filters="1">
      {!seller && show && (
        <Group id="categories" title={c.categories}>
          <CategoryTree show={show} lang={lang} category={category} sub={sub} onGo={goCategory} c={c} />
        </Group>
      )}
      <Group id="price" title={c.price}>
        <form className="mk-price-row" onSubmit={(e) => { e.preventDefault(); apply({ ...f, priceMin: price.min === '' ? null : Number(price.min), priceMax: price.max === '' ? null : Number(price.max) }); }}>
          <input aria-label={c.min} data-filter="min" inputMode="decimal" placeholder={c.min} value={price.min} onChange={(e) => setPrice({ ...price, min: e.target.value.replace(/[^\d.]/g, '') })} />
          <input aria-label={c.max} data-filter="max" inputMode="decimal" placeholder={c.max} value={price.max} onChange={(e) => setPrice({ ...price, max: e.target.value.replace(/[^\d.]/g, '') })} />
          <button type="submit" data-filter-apply="price">{c.apply}</button>
        </form>
      </Group>
      <Group id="moq" title={c.moq}>
        <Options attr="moq" c={c} kind="radio" onToggle={(v) => apply({ ...f, moqMax: v === 'any' ? null : Number(v) })}
          values={[{ value: 'any', label: c.any, selected: f.moqMax == null }, ...[1, 10, 50, 100, 500].map((n) => ({ value: String(n), label: c.moqUpTo(n), selected: f.moqMax === n }))]} />
      </Group>
      <Group id="rating" title={c.rating} defaultOpen={false}>
        <Options attr="rating" c={c} kind="radio" onToggle={(v) => apply({ ...f, minRating: v === 'any' ? null : Number(v) })}
          values={[{ value: 'any', label: c.any, selected: f.minRating == null }, ...[4.8, 4.5, 4].map((n) => ({ value: String(n), label: c.ratingAtLeast(n), selected: f.minRating === n }))]} />
      </Group>
      <Group id="buying" title={c.buying} defaultOpen={false}>
        <Options attr="buying" c={c} onToggle={(v) => apply(v === 'tiers' ? { ...f, hasTiers: f.hasTiers ? null : true } : { ...f, hasVariants: f.hasVariants ? null : true })}
          values={[{ value: 'tiers', label: c.tiers, selected: !!f.hasTiers }, { value: 'variants', label: c.variants, selected: !!f.hasVariants }]} />
      </Group>
      {groups.map((g, i) => (
        <Group key={g.attr} id={g.attr} title={g.label} note={g.match === 'all' ? c.allOf : null} defaultOpen={i < 4 || g.values.some((v) => v.selected)}>
          <Options attr={g.attr} c={c} values={g.values} onToggle={(v) => apply(toggleAttr(f, g.attr, v))} />
        </Group>
      ))}
    </div>
  );

  return (
    <MarketShell lang={lang} user={user} initialQuery={f.q} searchPlaceholder={scopeLabel ? c.searchIn(scopeLabel) : null}
      onSearch={(v) => (category ? apply({ ...f, q: v }) : nav(v ? `/market/search?q=${encodeURIComponent(v)}` : '/market/search'))}>
      <div className="mk-wrap" style={{ paddingBottom: 48 }}>
        <div className="mk-crumbs" aria-label="breadcrumb">
          <button type="button" onClick={() => nav('/market')}>{c.market}</button>
          {category && <><span aria-hidden="true">›</span>{sub ? <button type="button" onClick={() => nav(`/market/c/${category}`)}>{marketCategoryLabel(category, lang)}</button> : <span aria-current="page">{marketCategoryLabel(category, lang)}</span>}</>}
          {sub && <><span aria-hidden="true">›</span>{type ? <button type="button" onClick={() => nav(`/market/c/${category}/${sub}`)}>{marketLeafLabel(sub, lang)}</button> : <span aria-current="page">{marketLeafLabel(sub, lang)}</span>}</>}
          {type && <><span aria-hidden="true">›</span><span aria-current="page">{marketLeafLabel(type, lang)}</span></>}
          {!category && <><span aria-hidden="true">›</span><span aria-current="page">{seller ? c.supplier : f.q ? c.search : c.allProducts}</span></>}
        </div>
        <div className="mk-listing-head">
          <h1 data-results-title="1">{title}</h1>
          {SHOW_CATEGORY_COUNTS && <span className="mk-count" data-results-total={loading ? '' : total}>{loading ? '…' : c.results(total)}</span>}
        </div>
        {oldLink && <p className="mk-note" data-old-link="1">{c.oldLink}</p>}

        {!category && !seller && show && (
          <div className="mk-catchips" data-category-chips="1">
            {Object.entries(show).filter(([, v]) => v.total > 0).sort((a, b) => b[1].total - a[1].total).map(([code, v]) => (
              <button key={code} type="button" className="mk-subcat" data-catchip={code} onClick={() => goCategory(code)}>
                <Thumb refId={v.images?.[0]} /><b>{marketCategoryLabel(code, lang)}</b>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{v.total}</span>}
              </button>
            ))}
          </div>
        )}
        {category && tiles.length > 0 && (
          <div className="mk-subcats" data-leaf-chips={tiles.length} data-tiles={sub ? 'types' : 'subcategories'}>
            <button type="button" className="mk-subcat" aria-current={allTile.current} onClick={() => nav(`${allTile.to}${qs(writeFilters({ ...f, attrs: {} }))}`)}>
              <Thumb refId={allTile.images?.[0]} /><b>{c.all}</b>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{allTile.count}</span>}
            </button>
            {tiles.map((x) => (
              <button key={x.code} type="button" className="mk-subcat" data-leaf={x.code} aria-current={x.current} onClick={() => nav(`${x.to}${qs(writeFilters({ ...f, attrs: {} }))}`)}>
                <Thumb refId={x.images?.[0]} /><b>{x.label}</b>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{x.count}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="mk-layout">
          <aside className="mk-filters" aria-label={c.filters}>{panel}</aside>
          <div style={{ minWidth: 0 }}>
            <div className="mk-toolbar">
              <button type="button" className="mk-btn ghost mk-filter-toggle" data-open-filters="1" onClick={() => setDrawer(true)} style={{ height: 38 }}>
                <Icon.Filter />{c.filters}{nActive > 0 && <span className="mk-badge" style={{ position: 'static' }}>{nActive}</span>}
              </button>
              <label className="mk-sort">{c.sort}
                <select data-sort="1" value={f.sort} onChange={(e) => apply({ ...f, sort: e.target.value })}>
                  {SORTS.map((k) => <option key={k} value={k}>{k === 'relevance' && !f.q ? c.recommended : c.sorts[k]}</option>)}
                </select>
              </label>
            </div>
            {chips.length > 0 && (
              <div className="mk-chips" data-active-filters={chips.length}>
                {chips.map((x) => <button key={x.k} type="button" className="mk-chip" data-chip={x.k} onClick={() => apply(x.off)} title={x.label}><span>{x.label}</span><Icon.Close /></button>)}
                <button type="button" className="mk-chip clear" data-clear-filters="1" onClick={() => apply(clearFilters(f))}>{c.clearAll}</button>
              </div>
            )}
            {!loading && failed ? (
              <div data-load-error="1" style={{ textAlign: 'center', padding: '40px 0' }}>
                <p className="mk-empty" style={{ marginBottom: 12 }}>{c.loadError}</p>
                <button type="button" className="mk-btn ghost" data-retry-load="1" onClick={() => setRetryKey((x) => x + 1)}>{c.retry}</button>
              </div>
            ) : loading ? (
              <div className="mk-grid cols-4">{Array.from({ length: 8 }, (_, i) => <div key={i} className="mk-skel" style={{ aspectRatio: '3 / 4' }} />)}</div>
            ) : rows.length === 0 && srcQ && nActive === 0 ? null : rows.length === 0 ? (
              <div data-no-results="1">
                <p className="mk-empty">{c.noResults}{nActive > 0 && <><br /><button type="button" className="mk-more" onClick={() => apply(clearFilters(f))}>{c.clearTry}</button></>}</p>
                {!srcQ && <><div className="mk-sec-head"><h2>{c.noResultsTitle}</h2></div><ServicesPanel lang={lang} /></>}
              </div>
            ) : (
              <>
                <div onClickCapture={onResultClick}><CardRow rows={rows} lang={lang} user={user} exchangeRates={exchangeRates} max={rows.length} cols={4} /></div>
                {rows.length < total && (
                  <div ref={sentinel} style={{ textAlign: 'center', marginTop: 18 }}>
                    <button type="button" className="mk-btn ghost" data-load-more="1" disabled={moreBusy} onClick={more}>{moreBusy ? '…' : c.more}</button>
                  </div>
                )}
              </>
            )}
            {srcQ && !loading && !failed && (
              // Nothing in the catalog: the supplier results ARE the search results — no
              // "not found", no heading of their own. Otherwise they follow the catalog's.
              <section data-source-section={src.state} data-source-main={rows.length === 0 ? '1' : '0'} style={{ marginTop: rows.length === 0 ? 0 : 28 }}>
                {rows.length > 0 && <div className="mk-sec-head"><h2>{c.src.title}</h2></div>}
                {src.state === 'idle' && <button type="button" className="mk-btn ghost" data-source-search="1" onClick={() => runSource(srcQ)}>{c.src.btn}</button>}
                {src.state === 'loading' && (
                  <>
                    <p className="mk-count" data-source-loading="1" aria-live="polite">{rows.length === 0 ? c.src.finding(srcQ) : c.src.loading}</p>
                    <div className="mk-grid cols-4">{Array.from({ length: rows.length === 0 ? 8 : 4 }, (_, i) => <div key={i} className="mk-skel" style={{ aspectRatio: '3 / 4' }} />)}</div>
                  </>
                )}
                {src.state === 'error' && <p className="mk-cam-err static" role="alert" data-source-search-error="1">{src.err}</p>}
                {(src.state === 'done' || src.state === 'more') && (src.results.length
                  ? <>
                      <SourceResults results={src.results} lang={lang} exchangeRates={exchangeRates} />
                      {(src.more || src.state === 'more') && (
                        <div style={{ textAlign: 'center', marginTop: 18 }}>
                          <button type="button" className="mk-btn ghost" data-source-more="1" disabled={src.state === 'more'} onClick={() => runSource(src.q, src.page + 1)}>
                            {src.state === 'more' ? c.src.loading : c.more}
                          </button>
                        </div>
                      )}
                    </>
                  : rows.length === 0
                    ? <div data-source-none="1">
                        <p className="mk-empty">{c.noResults}</p>
                        <div className="mk-sec-head"><h2>{c.noResultsTitle}</h2></div><ServicesPanel lang={lang} />
                      </div>
                    : null)}
              </section>
            )}
          </div>
        </div>
      </div>
      {drawer && (
        <>
          <div className="mk-drawer-scrim" onClick={() => setDrawer(false)} />
          <div className="mk-drawer" role="dialog" aria-label={c.filters} data-filter-drawer="1">
            <div className="mk-drawer-head"><h2>{c.filters}</h2><button type="button" className="mk-icon-btn" onClick={() => setDrawer(false)} aria-label={c.close}><Icon.Close /></button></div>
            <div className="mk-drawer-body">{panel}</div>
            <div className="mk-drawer-foot">
              <button type="button" className="mk-btn ghost" onClick={() => apply(clearFilters(f))}>{c.clearAll}</button>
              <button type="button" className="mk-btn" data-drawer-show="1" onClick={() => setDrawer(false)}>{loading ? '…' : SHOW_CATEGORY_COUNTS ? c.show(total) : c.showAll}</button>
            </div>
          </div>
        </>
      )}
    </MarketShell>
  );
}
